import MirrorBackButton from "@/components/MirrorBackButton";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, Sparkles, Clock, Archive, TrendingUp, Star,
  ChevronDown, Share2, ArrowRight,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/* ── Mini Gauge ──────────────────────────────────────── */
const MiniGauge = ({ score, size = 40 }: { score: number; size?: number }) => {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const color =
    score >= 85 ? "hsl(187 72% 50%)" :
    score >= 70 ? "hsl(142 71% 45%)" :
    score >= 40 ? "hsl(40 90% 60%)" : "hsl(0 72% 51%)";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(222 18% 14%)" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-[11px] font-bold text-foreground">
        {score}
      </span>
    </div>
  );
};

/* ── Types ────────────────────────────────────────────── */
interface Snapshot {
  id: string;
  week_start: string;
  week_end: string;
  week_number: number;
  year: number;
  score: number | null;
  mood_avg: number | null;
  energy_avg: number | null;
  discipline_pct: number | null;
  confidence_pct: number | null;
  habits_completed: number;
  checkins_count: number;
  workouts_count: number;
  has_enough_data: boolean;
  highlight_text: string | null;
  badges: string[];
}

/* ── Page ────────────────────────────────────────────── */
const VYVMoments = () => {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        const { data } = await (supabase as any)
          .from("weekly_snapshots")
          .select("*")
          .eq("user_id", user.id)
          .order("week_start", { ascending: false })
          .limit(12);

        // Fetch insights for these weeks to get real highlight_text
        var weekStarts = (data || []).map((s: any) => s.week_start);
        var insightsMap: Record<string, { highlight_text: string; badges: string[] }> = {};
        if (weekStarts.length > 0) {
          try {
            var { data: insights } = await (supabase as any)
              .from("mirror_insights")
              .select("week_start, highlight_text, badges")
              .eq("user_id", user.id)
              .eq("insight_type", "weekly")
              .in("week_start", weekStarts)
              .order("generated_at", { ascending: false });
            for (var ins of (insights || [])) {
              if (!insightsMap[ins.week_start]) {
                insightsMap[ins.week_start] = { highlight_text: ins.highlight_text, badges: ins.badges || [] };
              }
            }
          } catch { /* mirror_insights may not exist yet */ }
        }

        setSnapshots((data || []).map((s: any) => {
          var insightData = insightsMap[s.week_start];
          return {
            ...s,
            highlight_text: insightData?.highlight_text || s.highlight_text || null,
            badges: insightData?.badges?.length > 0 ? insightData.badges : (s.badges || []),
          };
        }));
      } catch (e) {
        console.error("[VYVMoments]", e);
      }
      setLoading(false);
    };
    load();
  }, []);

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

  const latest = snapshots[0] || null;
  const hasSnapshots = snapshots.length > 0;
  const hasEnoughHistory = snapshots.length >= 4;

  /* Evolution chart */
  const chartScores = snapshots.filter(s => s.score !== null).map(s => s.score as number).reverse();
  const chartWeeks = snapshots.filter(s => s.score !== null).map(s => "W" + s.week_number).reverse();
  const chartW = 280;
  const chartH = 100;
  const canShowChart = chartScores.length >= 4;

  let pts: Array<{ x: number; y: number; s: number }> = [];
  let polyline = "";
  let areaPath = "";
  if (canShowChart) {
    const minS = Math.min(...chartScores) - 5;
    const maxS = Math.max(...chartScores) + 5;
    pts = chartScores.map((s, i) => {
      const x = (i / (chartScores.length - 1)) * chartW;
      const y = chartH - ((s - minS) / (maxS - minS)) * (chartH - 10);
      return { x, y, s };
    });
    polyline = pts.map(p => p.x + "," + p.y).join(" ");
    areaPath = "M" + pts[0].x + "," + chartH + " " + pts.map(p => "L" + p.x + "," + p.y).join(" ") + " L" + pts[pts.length - 1].x + "," + chartH + " Z";
  }

  /* On This Day comparisons */
  const fourWeeksAgo = snapshots.length >= 5 ? snapshots[4] : null;
  const twelveWeeksAgo = snapshots.length >= 12 ? snapshots[11] : null;

  /* Mirror Reflection delta */
  const firstScore = chartScores.length > 0 ? chartScores[0] : null;
  const lastScore = chartScores.length > 0 ? chartScores[chartScores.length - 1] : null;
  const scoreDelta = firstScore !== null && lastScore !== null ? lastScore - firstScore : null;

  return (
    <MobileLayout className="pb-28 pt-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center gap-3 mb-1">
        <MirrorBackButton />
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-primary" />
          <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/60">
            {t("moments.subtitle")}
          </p>
        </div>
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
        className="font-display text-[26px] font-semibold text-foreground leading-none mb-6">
        {t("moments.title")}
      </motion.h1>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {!loading && (
        <>
          {/* ── THIS WEEK'S MOMENT ────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}
            className="rounded-2xl p-5 mb-6"
            style={{
              background: "linear-gradient(135deg, #1a1a2e 0%, #0d1117 100%)",
              border: "1px solid hsl(187 72% 50% / 0.25)",
              boxShadow: "0 0 20px rgba(0,229,255,0.08)",
            }}>
            <p className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/55 mb-3">
              {t("moments.thisWeek")}
            </p>

            {!hasSnapshots ? (
              <>
                <h2 className="font-display text-[20px] font-bold text-foreground leading-tight mb-2">
                  {t("moments.momentsStartHere")}
                </h2>
                <p className="text-foreground/50 text-[12px] font-body leading-relaxed mb-4">
                  {t("moments.keepCheckingIn")}
                </p>
                <button onClick={() => navigate("/check-in")}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors">
                  <ArrowRight size={13} /> {t("moments.checkinNow")}
                </button>
              </>
            ) : (
              <>
                <span className="inline-block text-[10px] font-display font-medium px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary/70 mb-3">
                  {"W" + latest.week_number + " · " + latest.year}
                </span>

                {latest.has_enough_data && latest.score !== null ? (
                  <>
                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { emoji: "\uD83D\uDD25", label: t("score.title"), value: String(latest.score) },
                        { emoji: "\u2705", label: t("score.habits"), value: latest.habits_completed + "x" },
                        { emoji: "\uD83D\uDCAA", label: t("moments.workouts"), value: String(latest.workouts_count) },
                      ].map(s => (
                        <div key={s.label} className="text-center py-2 rounded-xl bg-foreground/[0.03] border border-border/20">
                          <span className="text-[14px]">{s.emoji}</span>
                          <p className="font-display text-[14px] font-bold text-foreground mt-0.5">{s.value}</p>
                          <p className="text-[9px] text-muted-foreground font-display">{s.label}</p>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => toast.info(t("moments.sharingSoon"))}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary/25 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/10 transition-colors">
                      <Share2 size={13} /> {t("moments.shareThisMoment")}
                    </button>
                  </>
                ) : latest.highlight_text ? (
                  <p className="text-foreground/60 text-[13px] font-body italic leading-relaxed">
                    {latest.highlight_text}
                  </p>
                ) : (
                  <p className="text-foreground/45 text-[12px] font-body italic">
                    {t("moments.weekInProgress")}
                  </p>
                )}
              </>
            )}
          </motion.div>

          {/* ── ON THIS DAY ───────────────────────────── */}
          {hasEnoughHistory && fourWeeksAgo && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={13} className="text-primary/60" />
                <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/55">
                  {t("moments.onThisDay")}
                </p>
              </div>

              {fourWeeksAgo.score !== null && (
                <div className="vyv-card p-4 mb-2">
                  <span className="text-[9px] font-display tracking-wide text-muted-foreground uppercase">
                    {tf("moments.weeksAgo", { n: "4" })}
                  </span>
                  <p className="text-foreground/55 text-[12px] font-body leading-relaxed mt-1.5">
                    {tf("moments.onThisDayCopy", { n: "4", score: String(fourWeeksAgo.score) })}
                  </p>
                </div>
              )}

              {twelveWeeksAgo && twelveWeeksAgo.score !== null && (
                <>
                  <div className="flex flex-col items-center py-1">
                    <div className="w-[1px] h-4 bg-primary/20" />
                    <ChevronDown size={14} className="text-primary/30 -my-1" />
                    <div className="w-[1px] h-4 bg-primary/20" />
                  </div>
                  <div className="vyv-card p-4 mb-2">
                    <span className="text-[9px] font-display tracking-wide text-muted-foreground uppercase">
                      {tf("moments.weeksAgo", { n: "12" })}
                    </span>
                    <p className="text-foreground/55 text-[12px] font-body leading-relaxed mt-1.5">
                      {tf("moments.onThisDayCopy", { n: "12", score: String(twelveWeeksAgo.score) })}
                    </p>
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 justify-center mt-1">
                <div className="w-2 h-2 rounded-full bg-primary" style={{ boxShadow: "0 0 6px hsl(187 72% 50%)" }} />
                <span className="text-[10px] font-display font-medium text-primary/60">{t("moments.now")}</span>
              </div>
            </motion.div>
          )}

          {/* ── PAST MOMENTS ──────────────────────────── */}
          {hasSnapshots && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Archive size={13} className="text-primary/60" />
                <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/55">
                  {t("moments.pastMoments")}
                </p>
              </div>
              <div className="space-y-2.5">
                {snapshots.map((pw, i) => {
                  const isOpen = expandedWeek === i;
                  return (
                    <button key={pw.id || i} onClick={() => setExpandedWeek(isOpen ? null : i)}
                      className="w-full text-left vyv-card p-4 transition-all duration-200">
                      <div className="flex items-center gap-3">
                        {pw.score !== null ? (
                          <MiniGauge score={pw.score} />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-foreground/[0.04] border border-border/20 flex items-center justify-center shrink-0">
                            <span className="text-[9px] text-muted-foreground font-display">—</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="font-display text-[13px] font-semibold text-foreground">{"W" + pw.week_number}</span>
                            <span className="text-[10px] text-muted-foreground font-display">{new Date(pw.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                          </div>
                          <p className="text-foreground/45 text-[11px] font-body italic mt-0.5 truncate">
                            {pw.highlight_text || t("moments.weekInProgress")}
                          </p>
                        </div>
                        <ChevronDown size={14} className={`text-muted-foreground transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                            className="overflow-hidden">
                            <p className="text-foreground/50 text-[12px] font-body italic leading-relaxed mt-3 mb-3">
                              {pw.highlight_text || t("moments.weekInProgress")}
                            </p>
                            {pw.badges.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {pw.badges.map((b: string, bi: number) => (
                                  <span key={bi} className="text-[9px] font-display px-2 py-0.5 rounded-full bg-primary/8 border border-primary/15 text-primary/60">
                                    {b}
                                  </span>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ── YOUR EVOLUTION ────────────────────────── */}
          {canShowChart && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(4)} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={13} className="text-primary/60" />
                <p className="text-[9px] font-display tracking-[0.22em] uppercase text-primary/55">
                  {t("moments.evolution")}
                </p>
              </div>
              <div className="vyv-card p-5">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-foreground/60 text-[12px] font-display font-medium">
                    {tf("moments.weekTrend", { n: String(chartScores.length) })}
                  </span>
                  {scoreDelta !== null && scoreDelta !== 0 && (
                    <span className="text-[11px] font-display font-semibold" style={{ color: scoreDelta > 0 ? "hsl(142 71% 45%)" : "hsl(0 72% 51%)" }}>
                      {(scoreDelta > 0 ? "\u2191 +" : "\u2193 ") + scoreDelta + "pts"}
                    </span>
                  )}
                </div>
                <svg width="100%" height={chartH + 20} viewBox={"0 0 " + chartW + " " + (chartH + 20)} preserveAspectRatio="none" className="overflow-visible">
                  <path d={areaPath} fill="url(#evolFill)" />
                  <defs>
                    <linearGradient id="evolFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(187 72% 50%)" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="hsl(187 72% 50%)" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>
                  <polyline fill="none" stroke="hsl(187 72% 50%)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    points={polyline} style={{ filter: "drop-shadow(0 0 4px hsl(187 72% 50% / 0.5))" }} />
                  {pts.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r="4" fill="hsl(187 72% 50%)" stroke="hsl(222 38% 6%)" strokeWidth="2" />
                  ))}
                  {pts.map((p, i) => (
                    <text key={i} x={p.x} y={chartH + 16} textAnchor="middle" fontSize="9" fill="hsl(218 14% 55%)" fontFamily="Sora, sans-serif">
                      {chartWeeks[i]}
                    </text>
                  ))}
                </svg>
              </div>
            </motion.div>
          )}

          {/* ── MIRROR REFLECTION ─────────────────────── */}
          {hasEnoughHistory && scoreDelta !== null && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={stagger(5)}
              className="rounded-2xl p-5 border border-primary/15"
              style={{ background: "linear-gradient(135deg, hsl(187 30% 9%), hsl(210 30% 8%))" }}>
              <div className="flex items-start gap-2.5 mb-4">
                <Star size={13} className="text-primary/60 mt-0.5 shrink-0" />
                <p className="text-foreground/55 text-[12px] font-body leading-relaxed italic">
                  {tf("moments.reflectionDynamic", { weeks: String(chartScores.length), from: String(firstScore), to: String(lastScore) })}
                </p>
              </div>
              <button onClick={() => navigate("/mirror")}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/10 border border-primary/15 text-primary/70 text-[12px] font-display font-medium hover:bg-primary/15 transition-colors">
                {t("moments.talkToMirror")} <ArrowRight size={13} />
              </button>
            </motion.div>
          )}
        </>
      )}

    </MobileLayout>
  );
};

export default VYVMoments;
